import { defineStore } from 'pinia';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import { usePropertyStore } from 'src/stores/propertyStore';
import { BookingEngineServiceDTO } from 'src/services/api/data-contracts';
import {
  ServiceInventory,
  ReservationItemService,
  IReservationItem,
} from 'src/types/common';

const apiService = Services.getService(Api);

export const useServiceStore = defineStore('serviceStore', {
  state: () => ({
    serviceInventoryByServiceId: {} as Record<number, ServiceInventory>,
    servicesPerRoomTypeAndRatePlan: {} as Record<
      string,
      BookingEngineServiceDTO[]
    >,
  }),
  getters: {},
  actions: {
    createKey(roomTypeId: number, ratePlanId: number) {
      const key = roomTypeId.toString() + '-' + ratePlanId.toString();
      return key;
    },
    clearCache() {
      this.serviceInventoryByServiceId = {};
      this.servicesPerRoomTypeAndRatePlan = {};
    },
    addResItemService(resX: ReservationItemService) {
      const obs: ServiceInventory =
        this.serviceInventoryByServiceId[resX.ServiceId];

      if (obs != null) {
        const resXFromService = obs.ReservationItems.filter(
          (u: ReservationItemService) => {
            return u.roomId == resX.roomId;
          },
        );

        if (resXFromService.length == 0) {
          obs.ReservationItems.push(resX);
        }
      }
    },
    removeResItemService(resX: ReservationItemService) {
      const obs: ServiceInventory =
        this.serviceInventoryByServiceId[resX.ServiceId];

      if (obs != null) {
        const resXFromService = obs.ReservationItems.filter(
          (u: ReservationItemService) => {
            return u.roomId == resX.roomId;
          },
        );

        if (resXFromService.length > 0) {
          const index = obs.ReservationItems.indexOf(resXFromService[0]);
          if (index > -1) {
            obs.ReservationItems.splice(index, 1);
          }
        }
      }
    },
    getAvailableServices(
      roomId: string,
      roomTypeId: number,
      ratePlanId: number,
    ): BookingEngineServiceDTO[] {
      const key = this.createKey(roomTypeId, ratePlanId);

      const servicesForRoomAndRate: BookingEngineServiceDTO[] =
        this.servicesPerRoomTypeAndRatePlan[key];

      const filteredServices = servicesForRoomAndRate.filter(
        (service: BookingEngineServiceDTO) => {
          const serviceId = service.ServiceId;
          let isAvailable = true;

          const obs: ServiceInventory =
            this.serviceInventoryByServiceId[serviceId];
          const availableInventory = obs.AvailableInventory;

          const roomAlreadyHasService = obs.ReservationItems.filter(
            (u: ReservationItemService) => {
              return u.roomId == roomId;
            },
          );

          isAvailable =
            availableInventory == null ||
            availableInventory > 0 ||
            roomAlreadyHasService.length > 0;

          return isAvailable;
        },
      );

      return filteredServices;
    },
    getReservationItemServices(reservationItem: IReservationItem) {
      const propertyStore = usePropertyStore();
      const propertyId = propertyStore.propertyId;

      reservationItem.areServicesLoaded = false;

      if (propertyId) {
        const query = {
          propertyId: propertyId,
          reservationItemId: reservationItem.ReservationItemId,
          languageCode: 'en',
        };

        const cachePromise =
          apiService.serviceGetReservationItemServices(query);
        cachePromise.then((cacheResponse) => {
          this.initializeServiceInventory(
            cacheResponse.data.InventoryPerServiceId,
          );

          reservationItem.hasSelectableServices =
            cacheResponse.data.Services.length > 0;

          const key = this.createKey(
            reservationItem.RoomTypeId,
            reservationItem.RatePlanId,
          );
          this.servicesPerRoomTypeAndRatePlan[key] =
            cacheResponse.data.Services.map((t) => {
              return t;
            });

          reservationItem.Services.forEach((ris: ReservationItemService) => {
            const serviceId = ris.ServiceId;
            let obs: ServiceInventory =
              this.serviceInventoryByServiceId[serviceId];
            if (obs == null) {
              obs = new ServiceInventory();
              obs.ServiceId = serviceId;
              obs.StartingInventory = null;
              this.serviceInventoryByServiceId[serviceId] = obs;
            }
          });

          reservationItem.areServicesLoaded = true;
        });
      }
    },

    getNewServices(reservationItem: IReservationItem) {
      const propertyStore = usePropertyStore();
      const propertyId = propertyStore.propertyId;

      reservationItem.areServicesLoaded = false;

      if (propertyId) {
        const query = {
          propertyId: propertyId,
          roomTypeId: reservationItem.RoomTypeId,
          ratePlanId: reservationItem.RatePlanId,
          dateOfArrival: reservationItem.DateOfArrival,
          dateOfDeparture: reservationItem.DateOfDeparture,
          languageCode: 'en',
        };

        const cachePromise = apiService.serviceGet(query);
        cachePromise.then((cacheResponse) => {
          this.initializeServiceInventory(
            cacheResponse.data.InventoryPerServiceId,
          );
          const key = this.createKey(
            reservationItem.RoomTypeId,
            reservationItem.RatePlanId,
          );
          reservationItem.hasSelectableServices =
            cacheResponse.data.Services.length > 0;

          this.servicesPerRoomTypeAndRatePlan[key] =
            cacheResponse.data.Services.map((t) => {
              return t;
            });

          reservationItem.areServicesLoaded = true;
        });
      }
    },
    updateServiceInventoryByServiceId(
      serviceId: number,
      inventory: null | number,
    ) {
      let obs: ServiceInventory = this.serviceInventoryByServiceId[serviceId];
      if (obs == null) {
        obs = new ServiceInventory();
        obs.ServiceId = serviceId;
        obs.StartingInventory = inventory;
        this.serviceInventoryByServiceId[serviceId] = obs;
      } else {
        obs.StartingInventory = inventory;
      }
    },

    initializeServiceInventory(
      serviceInventoryByServiceId: Record<number, number>,
    ) {
      for (const key in serviceInventoryByServiceId) {
        if (serviceInventoryByServiceId.hasOwnProperty(key)) {
          const serviceId = Number.parseInt(key);
          const inventory = serviceInventoryByServiceId[key];

          this.updateServiceInventoryByServiceId(serviceId, inventory);
        }
      }
    },
  },
});
